import React, { useState, useCallback, useContext } from 'react'
// import React, { useState, useEffect } from 'react'
import styled from '@emotion/styled'
import tw from 'twin.macro'
import PropTypes from 'prop-types'
import { Waypoint } from 'react-waypoint';
import { motion, useViewportScroll, useTransform, useMotionTemplate } from "framer-motion"

import Container from '../global/Container'
import HTML from '../global/HTML'
import Button from '../global/Button'
import Circle from '../global/Circle'
import hexToRgba from 'hex-to-rgba';

import DisplayContext from '../../contexts/DisplayContext'

const AudienceComponent = ({ audience, data, className }) => {

  const display = useContext(DisplayContext);

  const [elementTop, setElementTop] = useState(0);
  const { scrollY } = useViewportScroll()

  const _sectionScale = useTransform(scrollY, value => (value - elementTop) <= 1 ? 1 : 1 + ((value - elementTop) * 0.0001))

  const sectionScale = display == 'live' ? useMotionTemplate`scale(${_sectionScale})` : 'none'

  const measuredRef = useCallback(node => {
    if (node !== null) {
      setElementTop(node.getBoundingClientRect().top);
    }
  }, []);

  const [circlesPosition, setCirclesPosition] = useState('below');

  const activeCircles = {
    inside: {
      opacity: 1,
      y: 0,
    },
    above: { 
      opacity: 1,
      y: 0,
    },
  }

  const inactiveCircles = display == 'live' ? {
    below: { opacity: 0, y: 0, },
  } : {
    below: { opacity: 1, y: 0, },
  }

  const animateCircles = Object.assign(activeCircles, inactiveCircles)

  const handleCirclesPos = (currentPosition) => {
    setCirclesPosition(currentPosition)
  }

  const circlesCol = data.Circles.map((circle, index) => {
    return (
      <Circle key={index} size={320} floating={false}>
        <span>
          <strong><HTML content={circle.heading} /></strong>
          <HTML content={circle.text} />
        </span>
      </Circle>
    );
  });

  return (
    <div className={className} ref={measuredRef}>
      <Waypoint 
        scrollableAncestor={typeof window !== 'undefined' && window}
        onEnter={({ currentPosition }) => {
          handleCirclesPos(currentPosition)
        }}
        onLeave={({ currentPosition }) => {
          handleCirclesPos(currentPosition)
        }}
      >
        <span className="circles-intersect-ref" />
      </Waypoint>
      <span className={audience}>
        <div className="audience-text">
          <Container>
            <h1>{data.heading}</h1>
          </Container>
        </div>
        <motion.div className="circles"
          animate={circlesPosition}
          variants={animateCircles}
          transition={{ duration: 0.5 }}
          initial="below"
          > 
          <Container> 
            {circlesCol}
          </Container>
        </motion.div>
        <div className="learn-more">
          <Container>
            <Button link={`/${audience}`}>
              Learn More &nbsp;
              <svg width="10" height="16" viewBox="0 0 10 16" fill="none" xmlns="http://www.w3.org/2000/svg"><path d="M1.51953 1.474L8.4546 8.40906L1.51953 15.3441" stroke="#00ADEE" strokeWidth="1.73377" strokeLinejoin="round"/></svg>
            </Button>
          </Container>
        </div>
        
        <motion.div 
          className="foreground" 
          style={{ 
            transform: sectionScale, 
            backgroundImage: `url(${
              data.foreground.childImageSharp
                ? data.foreground.childImageSharp.fluid.src
                : data.foreground
            })`
          }}>
        </motion.div>
        <motion.div
          className="background"
          style={{ 
            transform: sectionScale,
            backgroundImage: `url(${
              data.background.childImageSharp
                ? data.background.childImageSharp.fluid.src
                : data.background
            })`
          }}
        />
      </span>
    </div>
  )
}

AudienceComponent.propTypes = {
  className: PropTypes.string.isRequired,
  data: PropTypes.object.isRequired,
  audience: PropTypes.string.isRequired,
}

const Audience = styled(AudienceComponent)`
  ${tw`overflow-hidden bg-cover relative w-full`}
  height: 100vh;
  min-height: 900px;
  max-height: 1200px;
  @media (max-width: 1250px) {
    min-height: auto;
    height: 900px;
  }
  @media (max-width: 767px) {
    height: 700px;
  }
  .foreground, .background {
    ${tw`overflow-hidden bg-cover absolute w-full`}
    height: 100%;
    top: 0;
    background-position: center;
  }
  .foreground { 
    z-index: 3; 
    backface-visibility: hidden;
  }
  .background { 
    z-index: 1; 
    backface-visibility: hidden;
  }
  .audience-text {
    ${tw`w-full absolute text-white`}
    z-index: 2;
    top: 14%;
    left: 0;
    h1 {
      font-size: 140px;
      line-height: 90%;
      @media (max-width: 1250px) {
        font-size: 100px;
      }
      @media (max-width: 767px) {
        font-size: 70px;
      }
      @media (max-width: 479px) {
        font-size: 54px;
      }
    }
  }
  .businesses .audience-text {
    top: 6%;
  }
  .schools .audience-text {
    top: 17%;
  }
  .circles-intersect-ref {
    position: absolute;
    z-index: 3;
    top: 50%;
    height: 1px;
  }
  .view-intersect-ref {
    position: absolute;
    z-index: 2;
    top: 100%;
    height: 1px;
  }
  .learn-more {
    position: absolute;
    z-index: 40;
    bottom: 100px;
    width: 100%;
    text-align: center;
    @media (max-width: 1023px) {
      text-align: left;
    }
    @media (max-width: 639px) {
      bottom: 40px;
      transform: scale(0.8);
      text-align: center;
    }
  }
  .circles {
    position: absolute;
    width: 100%;
    backface-visibility: hidden;
    z-index: 5;
    @media (max-width: 639px) {
      > div {
        transform: scale(0.6) !important;
        width: 512px;
      }
    }
  }
  .individuals .circles {
    transform: scale(1.3);
    left: 20%;
    bottom: 220px;
    @media (max-width: 1250px) {
      left: 10%;
      bottom: 100px;
    }
    @media (max-width: 767px) {
      > div {
        transform: scale(0.8);
        transform-origin: left;
      }
    }
    @media (max-width: 479px) {
      left: 22%;
    }
  }
  .individuals .circles .circle {
    margin-left: -150px;
  }
  .individuals .circles svg:nth-of-type(2) {
    margin-left: -220px;
    margin-bottom: 200px;
  }
  .circles svg:nth-of-type(3) .content {
    background-color: ${hexToRgba('FF81F2', 0.8)} !important;
  }
  .circles svg:nth-of-type(2) .content {
    background-color: ${hexToRgba('D9CFC7', 0.8)} !important;
  }
  .businesses .circles {
    transform: scale(1.1);
    margin-left: 50%;
    bottom: 140px;
    @media (max-width: 1024px) {
      margin-left: 40%;
      bottom: 100px;
    }
    @media (max-width: 767px) {
      bottom: 0;
      > div {
        transform: scale(0.8);
        transform-origin: left;
      }
    }
    @media (max-width: 479px) {
      margin-left: 22%;
    }
  }
  .businesses .circles .circle {
    margin-left: -150px;
  }
  .businesses .circles svg:nth-of-type(2) {
    margin-left: -220px;
    margin-bottom: 270px;
  }
  .schools .circles {
    transform: scale(1.2);
    margin-left: 50%;
    bottom: 140px;
    @media (max-width: 1024px) {
      margin-left: 40%;
      bottom: 100px;
    }
    @media (max-width: 767px) {
      bottom: 0;
      > div {
        transform: scale(0.8);
        transform-origin: left;
      }
    }
    @media (max-width: 479px) {
      margin-left: 22%;
    }
  }
  .schools .circles .circle {
    margin-left: -150px;
  }
  .schools .circles svg:nth-of-type(2) {
    margin-left: -220px;
    margin-bottom: 270px;
  }
`

export default Audience
